import type { HttpInterceptorFn } from '@angular/common/http';
import { HttpContextToken } from '@angular/common/http';
import { inject } from '@angular/core';

import { AppConfigService } from '../config/app-config.service';

/**
 * Token de contexto que expone el trace-id generado, para que el interceptor de
 * error pueda correlacionar el toast con el request en los logs del backend.
 */
export const TRACE_ID = new HttpContextToken<string>(() => '');

function newTraceId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `trace-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Inyecta un id de correlación único por request (observabilidad distribuida).
 * Debe correr PRIMERO para que error/retry lo vean en el contexto.
 *
 * Se mandan DOS headers con el MISMO valor:
 *  - `X-Trace-Id`: id propio del front (lo usan retry/error/toast para correlacionar).
 *  - `X-Request-Id`: id que el backend Nest respeta en el borde CLS si es válido
 *    (UUID) → el request-id del backend queda encadenado al del front con un SOLO
 *    id, así un error del cliente se rastrea de punta a punta en los logs del server.
 */
export const traceIdInterceptor: HttpInterceptorFn = (req, next) => {
  const config = inject(AppConfigService);
  const cfg = config.config();

  const isOwnApi =
    req.url.startsWith(cfg.apiBaseUrl) ||
    (cfg.apiFailoverUrl ? req.url.startsWith(cfg.apiFailoverUrl) : false);
  if (!isOwnApi) return next(req);

  const traceId = newTraceId();
  req.context.set(TRACE_ID, traceId);

  return next(
    req.clone({ setHeaders: { 'X-Trace-Id': traceId, 'X-Request-Id': traceId } }),
  );
};
